import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import API from '../api';
import { useLang } from '../LangContext';

const Reveal = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
    >
      {children}
    </motion.div>
  );
};

function formatPhone(phone) {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  if (digits.startsWith('251')) return '+' + digits;
  if (digits.startsWith('0')) return '+251' + digits.slice(1);
  if (digits.length === 9 && (digits.startsWith('9') || digits.startsWith('7'))) return '+251' + digits;
  return phone;
}

export default function ClientDetail() {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [activeImg, setActiveImg] = useState(null);
  const { t, lang } = useLang();

  const getField = (en, am, or_) => (lang === 'am' && am) ? am : (lang === 'or' && or_) ? or_ : en;

  useEffect(() => {
    API.get(`/clients/${id}`).then(r => setClient(r.data));
  }, [id]);

  if (!client) return <div style={{ textAlign: 'center', padding: 100, color: 'var(--primary)', fontWeight: 700 }}>{t.loading}</div>;

  return (
    <div className="page" style={{ paddingTop: 100 }}>
      <div className="hero-gradient" style={{ height: '30vh' }} />
      
      <Reveal>
        <Link to="/clients" style={{ color: 'var(--primary)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 32 }}>
          {t.backToClients}
        </Link>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginBottom: 40, flexWrap: 'wrap' }}>
          <div style={{ 
            width: 100, height: 100, 
            background: 'var(--bg-subtle)', 
            borderRadius: 24, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            fontSize: 48, 
            flexShrink: 0, 
            overflow: 'hidden',
            border: '1px solid rgba(0,0,0,0.05)'
          }}>
            {client.logo_url ? <img src={client.logo_url} alt={client.name} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 10 }} /> : client.icon || '🏢'}
          </div>
          <div>
            <h1 style={{ fontSize: 'clamp(22px, 3.5vw, 30px)', fontWeight: 900, marginBottom: 8 }}>{client.name}</h1>
            <span style={{ background: 'rgba(255, 122, 0, 0.1)', color: 'var(--primary)', padding: '6px 16px', borderRadius: 100, fontSize: 17, fontWeight: 800 }}>
              {getField(client.category, client.category_am, client.category_or)}
            </span>
          </div>
        </div>

        <p style={{ color: 'var(--text-main)', lineHeight: 1.6, marginBottom: 48, maxWidth: 800 }}>
          {getField(client.full_description, client.full_description_am, client.full_description_or)}
        </p>
      </Reveal>

      <div className="grid-3" style={{ marginBottom: 48 }}>
        {[
          ['📅', t.established, client.established],
          ['📍', t.location, client.location],
          ['🏷️', t.businessTypeLabel, client.business_type || client.category],
        ].map(([icon, label, val], i) => (
          <Reveal key={label} delay={i * 0.1}>
            <div className="creative-card" style={{ padding: 24 }}>
              <div style={{ fontSize: 24, marginBottom: 12 }}>{icon}</div>
              <div style={{ color: 'var(--text-muted)', fontSize: 17, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{label}</div>
              <div style={{ fontWeight: 800, fontSize: 18, marginTop: 4, color: 'var(--secondary)' }}>{val || '—'}</div>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal>
        <div style={{ 
          background: 'rgba(34, 197, 94, 0.05)', 
          border: '1px solid rgba(34, 197, 94, 0.2)', 
          borderRadius: 24, 
          padding: 24, 
          marginBottom: 48, 
          display: 'flex', 
          alignItems: 'center', 
          gap: 20 
        }}>
          <div style={{ width: 56, height: 56, background: '#22c55e', color: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 }}>✓</div>
          <div>
            <div style={{ color: '#22c55e', fontWeight: 800, fontSize: 18 }}>{t.verifiedLicensed}</div>
            <div style={{ color: 'var(--text-muted)', fontSize: 17 }}>{t.licenseLabel}: <span style={{ color: 'var(--secondary)', fontWeight: 700 }}>{client.business_license_number || '—'}</span></div>
          </div>
        </div>
      </Reveal>

      {(client.phone || client.facebook || client.instagram || client.tiktok || client.telegram || client.website) && (
        <Reveal>
          <div className="creative-card" style={{ marginBottom: 48 }}>
            <h3 style={{ fontSize: 18, fontWeight: 800, marginBottom: 24, color: 'var(--secondary)' }}>{t.contactSocial}</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {client.phone && (
                <a href={`tel:${formatPhone(client.phone)}`} style={contactChip('#22c55e')}>
                  📞 {formatPhone(client.phone)}
                </a>
              )}
              {client.facebook && (
                <a href={client.facebook} target="_blank" rel="noreferrer" style={contactChip('#1877F2')}>
                  📘 Facebook
                </a>
              )}
              {client.instagram && (
                <a href={client.instagram} target="_blank" rel="noreferrer" style={contactChip('#E1306C')}>
                  📸 Instagram
                </a>
              )}
              {client.tiktok && (
                <a href={client.tiktok} target="_blank" rel="noreferrer" style={contactChip('#000')}>
                  🎵 TikTok
                </a>
              )}
              {client.telegram && (
                <a href={client.telegram} target="_blank" rel="noreferrer" style={contactChip('#29A8E0')}>
                  ✈️ Telegram
                </a>
              )}
              {client.website && (
                <a href={client.website} target="_blank" rel="noreferrer" style={contactChip('#FF7A00')}>
                  🌐 Website
                </a>
              )}
            </div>
          </div>
        </Reveal>
      )}

      {client.images?.length > 0 && (
        <section style={{ marginBottom: 48 }}>
          <Reveal><h2 style={{ fontSize: 28, fontWeight: 800, marginBottom: 32 }}>{t.ourMarketingWork}</h2></Reveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 20 }}>
            {client.images.map((img, i) => (
              <Reveal key={img.id} delay={i * 0.05}>
                <div className="creative-card" style={{ padding: 0 }}>
                  <div onClick={() => setActiveImg(img.url)} style={{ cursor: 'zoom-in', aspectRatio: '1/1' }}>
                    <img src={img.url} alt={img.description} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                  {(img.price || img.description) && (
                    <div style={{ padding: 16 }}>
                      {img.price && <div style={{ color: 'var(--primary)', fontWeight: 800, fontSize: 18 }}>ETB {Number(img.price).toLocaleString()}</div>}
                      {img.description && <div style={{ fontSize: 16, color: 'var(--text-muted)', marginTop: 4 }}>{img.description}</div>}
                    </div>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {activeImg && (
        <div onClick={() => setActiveImg(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(30, 41, 59, 0.9)', backdropFilter: 'blur(10px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out', padding: 40 }}>
          <motion.img 
            initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            src={activeImg} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 20, boxShadow: '0 50px 100px rgba(0,0,0,0.3)' }} 
          />
          <button style={{ position: 'absolute', top: 32, right: 32, background: '#fff', border: 'none', width: 48, height: 48, borderRadius: '50%', fontSize: 24, cursor: 'pointer' }}>✕</button>
        </div>
      )}
    </div>
  );
}

const contactChip = (color) => ({
  display: 'inline-flex', alignItems: 'center', gap: 8,
  padding: '10px 20px', borderRadius: 100,
  background: '#fff', border: '1px solid rgba(0,0,0,0.05)',
  color, fontWeight: 800, fontSize: 17,
  textDecoration: 'none', transition: 'all 0.2s',
  boxShadow: '0 2px 10px rgba(0,0,0,0.02)'
});
